import React, { useEffect, useRef, useState } from "react";
import { Icon, styles as ui } from "stories/Admin";
import styles from "./MawaqeetGenerateTimes.module.scss";
import { loadGoogleMapsScript } from "utils/googleMapsLoader";

interface PlacesAutocompleteProps {
  onPlaceSelected: (place: { name: string; lat: number; lon: number }) => void;
  placeholder?: string;
}

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

function readLatLng(location: any): { lat: number; lon: number } | null {
  if (!location) return null;
  const latRaw =
    typeof location.lat === "function" ? location.lat() : location.lat;
  const lngRaw =
    typeof location.lng === "function" ? location.lng() : location.lng;
  const lat = typeof latRaw === "number" ? latRaw : Number(latRaw);
  const lon = typeof lngRaw === "number" ? lngRaw : Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat === 0 && lon === 0) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

function placeDisplayName(place: any, fallback: string): string {
  const name = place?.displayName;
  if (typeof name === "string" && name.trim()) return name.trim();
  if (name?.text && typeof name.text === "string") return name.text.trim();
  return fallback;
}

async function geocodePlaceName(
  name: string,
): Promise<{ lat: number; lon: number } | null> {
  if (!window.google?.maps?.Geocoder) return null;
  const geocoder = new window.google.maps.Geocoder();
  try {
    const result = await geocoder.geocode({ address: name });
    const loc = result?.results?.[0]?.geometry?.location;
    return readLatLng(loc);
  } catch {
    return null;
  }
}

/** Åpen geokoding-fallback når Google ikke gir koordinater. */
async function geocodeWithOpenMeteo(
  name: string,
): Promise<{ lat: number; lon: number } | null> {
  try {
    const url =
      "https://geocoding-api.open-meteo.com/v1/search?" +
      new URLSearchParams({
        name,
        count: "1",
        language: "en",
        format: "json",
      }).toString();
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const first = data?.results?.[0];
    if (!first) return null;
    const lat = Number(first.latitude);
    const lon = Number(first.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  } catch {
    return null;
  }
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  onPlaceSelected,
  placeholder = "Søk etter by …",
}) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef = useRef<any>(null);

  useEffect(() => {
    loadGoogleMapsScript().then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (ready && window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [ready]);

  const handleSearch = async (input: string) => {
    if (!ready || input.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const { suggestions } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input,
            includedPrimaryTypes: ["locality", "administrative_area_level_3"],
            sessionToken: sessionTokenRef.current,
          },
        );

      const mapped: Suggestion[] = suggestions.map((s: any) => ({
        placeId: s.placePrediction.placeId,
        mainText: s.placePrediction.mainText?.text || "",
        secondaryText: s.placePrediction.secondaryText?.text || "",
      }));

      setSuggestions(mapped);
      setShowSuggestions(mapped.length > 0);
    } catch (err) {
      console.error("AutocompleteSuggestion feilet:", err);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(value), 300);
  };

  const handleSelect = async (suggestion: Suggestion) => {
    setIsLoading(true);
    setShowSuggestions(false);
    setQuery(suggestion.mainText);

    try {
      const place = new window.google.maps.places.Place({
        id: suggestion.placeId,
      });

      await place.fetchFields({ fields: ["displayName", "location"] });

      const name = placeDisplayName(place, suggestion.mainText);
      let coords = readLatLng(place.location);

      if (!coords) {
        coords = await geocodePlaceName(
          [suggestion.mainText, suggestion.secondaryText]
            .filter(Boolean)
            .join(", "),
        );
      }
      if (!coords) {
        coords = await geocodeWithOpenMeteo(
          [suggestion.mainText, suggestion.secondaryText]
            .filter(Boolean)
            .join(", "),
        );
      }

      if (!coords) {
        console.error("Fant ingen koordinater for sted:", name);
        onPlaceSelected({ name, lat: Number.NaN, lon: Number.NaN });
        return;
      }

      console.log("Valgt sted:", { name, ...coords });
      onPlaceSelected({ name, lat: coords.lat, lon: coords.lon });

      if (window.google.maps.places.AutocompleteSessionToken) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }
    } catch (err) {
      console.error("Place.fetchFields feilet:", err);
      // Autocomplete fungerte, men details feilet — prøv geokoding på navnet.
      const name = suggestion.mainText;
      const coords =
        (await geocodePlaceName(
          [suggestion.mainText, suggestion.secondaryText]
            .filter(Boolean)
            .join(", "),
        )) ||
        (await geocodeWithOpenMeteo(
          [suggestion.mainText, suggestion.secondaryText]
            .filter(Boolean)
            .join(", "),
        ));
      if (coords) {
        onPlaceSelected({ name, lat: coords.lat, lon: coords.lon });
      } else {
        onPlaceSelected({ name, lat: Number.NaN, lon: Number.NaN });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.places}>
      <span className={styles.placesIcon} aria-hidden="true">
        <Icon name="search" size={18} strokeWidth={2} />
      </span>
      <input
        type="text"
        className={ui.inp}
        aria-label="Søk etter by"
        value={query}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={ready ? placeholder : "Laster …"}
        disabled={!ready || isLoading}
      />

      {showSuggestions && suggestions.length > 0 && (
        <ul className={styles.suggestions} role="listbox">
          {suggestions.map((s) => (
            <li key={s.placeId} role="option" aria-selected={false}>
              <button type="button" onClick={() => handleSelect(s)}>
                <b>{s.mainText}</b>
                <span className={ui.help}>{s.secondaryText}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
